import React from "react";
import {
  RiCalendar2Line,
  RiCalendarEventLine,
  RiRefreshLine,
  RiCheckLine,
} from "react-icons/ri";

const MonthPicker = ({
  setTotal,
  currentYear,
  currentMonth,
  loadStatistics,
  setCurrentYear,
  setCurrentMonth,
  setLocalSetStats,
}) => {
  const YEARS = Array.from({ length: 4 }).map(
    (_, i) => new Date().getFullYear() - 3 + i
  );

  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // small visual helpers
  const tones = [
    "from-emerald-500 to-teal-500",
    "from-indigo-500 to-purple-500",
    "from-amber-500 to-red-500",
    "from-sky-500 to-indigo-500",
  ];
  const monthAbbr = (m) => m.slice(0, 3);

  const resetToToday = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    setLocalSetStats(null);
    setTotal(0);
    setCurrentYear(y);
    setCurrentMonth(m);
    loadStatistics({ year: y, month: m });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header / toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-900 text-white">
            <RiCalendar2Line />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Filter by period</p>
            <p className="text-[11px] text-gray-500">
              Choose a year and a month to load statistics
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={resetToToday}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-800 hover:bg-gray-50"
          title="Reset to current month"
        >
          <RiRefreshLine className="text-gray-700" />
          Reset to today
        </button>
      </div>

      {/* Year selector */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-700">
              <RiCalendarEventLine />
            </span>
            <h3 className="text-sm font-semibold text-gray-900">Select a year</h3>
          </div>
          <span className="text-[11px] text-gray-500">
            Current: <strong className="text-gray-900">{currentYear}</strong>
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {YEARS.map((year, idx) => {
            const selected = currentYear === year;
            const tone = tones[idx % tones.length];
            return (
              <div
                key={year}
                className={`rounded-xl p-[1px] bg-gradient-to-br ${tone} ${selected ? "opacity-100" : "opacity-80"
                  } transition-opacity`}
              >
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setLocalSetStats(null);
                    setTotal(0);
                    setCurrentYear(year);
                    loadStatistics({ year, month: currentMonth });
                  }}
                  className={`w-full rounded-[10px] px-4 py-2 text-sm font-semibold transition
                    ring-1 ring-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900
                    ${selected ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50"}`}
                >
                  <span className="inline-flex items-center gap-2">
                    {selected && <RiCheckLine className="text-white/90" />}
                    {year}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Month selector */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-700">
              <RiCalendarEventLine />
            </span>
            <h3 className="text-sm font-semibold text-gray-900">Select a month</h3>
          </div>
          <span className="text-[11px] text-gray-500">
            Current:{" "}
            <strong className="text-gray-900">
              {MONTHS[currentMonth - 1]}
            </strong>
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {MONTHS.map((month, index) => {
            const selected = currentMonth - 1 === index;
            const tone = tones[index % tones.length];
            return (
              <div
                key={month}
                className={`rounded-xl p-[1px] bg-gradient-to-br ${tone} ${selected ? "opacity-100" : "opacity-80"
                  } transition-all`}
              >
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setLocalSetStats(null);
                    setTotal(0);
                    setCurrentMonth(index + 1);
                    loadStatistics({ month: index + 1, year: currentYear });
                  }}
                  title={`Select ${month}`}
                  className={`w-full rounded-[10px] px-3 py-2 text-sm font-semibold transition
                    ring-1 ring-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900
                    ${selected ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50"}`}
                >
                  <span className="flex items-center justify-between">
                    {/* Left: tiny avatar-like badge */}
                    <span
                      className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-[11px] font-bold
                        ${selected ? "bg-white text-gray-900" : "bg-gray-100 text-gray-700"}`}
                    >
                      {monthAbbr(month)}
                    </span>

                    {/* Center: label */}
                    <span className="mx-2 truncate">{month}</span>

                    {/* Right: current marker */}
                    {selected ? (
                      <span className="inline-flex items-center gap-1 text-[11px]">
                        <RiCheckLine className="opacity-90" />
                        Current
                      </span>
                    ) : (
                      <span className="text-[11px] text-gray-400">Pick</span>
                    )}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default MonthPicker;
